import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { ErrorMessage, Typeahead } from '@gsa/afp-component-library';
import { Controller } from 'react-hook-form';
import { GET_OPTIONS } from '../../services/data-store.gql';

let userTyped = false;
let yearFilter = '';
let programFilter = null;

export const EquipmentCodeTypeAhead = ({
  onChange,
  value = 0,
  year = '',
  program = null,
}) => {
  const [option, setOption] = useState({ values: [], raw: [] });
  const [reset, setReset] = useState(false);
  const [userInput, setUserInput] = useState('');
  let error = '';
  yearFilter = year;
  programFilter = program;

  // Executes when the Year filter changes.
  useEffect(() => {
    setReset(true);
    onChange({ selectedData: { id: '0' } });
    setOption({ values: [], raw: [] });
  }, [yearFilter, programFilter]);

  // Executes when the text changes with in the TypeAhead.
  useEffect(() => {
    if (userInput?.length <= 1 && value > 0) {
      setUserInput('');
      error = '';
      onChange({ selectedData: { id: '0' } });
    }
  }, [userInput]);

  // Determines the error message based on the error code.
  const optionvalue = option?.values[0];
  if (optionvalue === 'Error: 404') {
    error = 'Equipment Code was not found for the selected Contract year.';
  } else if (optionvalue === 'Error: emptyYear') {
    error = 'Please select a Contract year';
  } else {
    error = '';
  }

  // Get the options to select when the user enters text in the type ahead.
  const [getOptions] = useLazyQuery(GET_OPTIONS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      let result = { values: [], raw: [] };
      if (data?.getOptions) {
        result = {
          values: data.getOptions.map((v) => v.value),
          raw: data.getOptions,
        };
      }

      // Determine what needs to be sent as a response to display in the Error Message for the TypeAhead.
      setOption(() => {
        if (result.values.length > 0) {
          return result;
        }
        if (userInput?.length === 0) {
          return {
            values: ['Error: emptyKeyword'],
            raw: ['Error: emptyKeyword'],
          };
        }
        if (year === '') {
          return { values: ['Error: emptyYear'], raw: ['Error: emptyYear'] };
        }
        return { values: ['Error: 404'], raw: ['Error: 404'] };
      });
    },
    onError: () => {
      //  TODO - intentionally blank now
    },
  });

  EquipmentCodeTypeAhead.propTypes = {
    value: PropTypes.number,
    year: PropTypes.string,
    program: PropTypes.string,
    onChange: PropTypes.func.isRequired,
  };

  return (
    <div className="margin-top-1">
      <Typeahead
        id="copyFromEquipmentCodeId"
        role="textbox"
        name="from associated equipment code"
        filterValue=""
        typeaheadValues={option?.values}
        onFilterKeyDown={(e) => {
          // Executes when the user types text in the type ahead.
          userTyped = true;
          setReset(false);
          setUserInput(e.target.value);
        }}
        onOptionEnter={(selected) => {
          // Executes the following when the user selects a option from the returned options.
          const selectedData = option?.raw.find((o) => o.label === selected);
          onChange({ selected, selectedData });
        }}
        fetchTypeaheadValues={(_, search) => {
          // Executes when text changes in the type ahead and fetches the values.
          const keyword = search.trim();
          if (userTyped) {
            const filterValues = [
              { operator: 'EQ', key: 'year', value: yearFilter },
              { operator: 'LIKE', key: 'code', value: keyword },
              { operator: 'EQ', key: 'deletedAt', value: null },
            ];
            if (programFilter)
              filterValues.push({
                operator: 'EQ',
                key: 'program',
                value: programFilter,
              });
            getOptions({
              variables: {
                model: 'EquipmentCode',
                label: 'code',
                value: 'code',
                // use default limit 50
                filter: {
                  operator: 'AND',
                  value: filterValues,
                },
                order: [
                  ['codeStartsWith', search],
                  ['code', 'ASC'],
                ],
              },
            });
            userTyped = false;
          }
        }}
        debounceDelay={250}
        inputCharNum={0}
        errorMessage={error}
        forceUpdate={reset ? { value: '' } : null}
        onClear={(selected) => {
          onChange({ selected, undefined });
        }}
      />
    </div>
  );
};

const EquipmentCode = ({
  isNew = false,
  errors,
  control,
  setValue,
  year = '',
  program = null,
  nameValue,
  watch,
  clearErrors,
}) => {
  return (
    <div className="grid-row padding-bottom-2">
      <div className="grid-col-10">
        {isNew && (
          <>
            <h2 className="font-sans-xs">Associated Equipment Code</h2>
            <EquipmentCodeTypeAhead
              value={watch('copyFromEquipmentCodeId')}
              year={year}
              program={program}
              onChange={({ selectedData }) => {
                setValue(nameValue, parseInt(selectedData?.id, 10));
                clearErrors('copyFromEquipmentCodeId');
              }}
            />
            {errors?.[nameValue]?.message && (
              <ErrorMessage>{errors?.[nameValue]?.message}</ErrorMessage>
            )}
            <Controller
              control={control}
              name={nameValue}
              render={({ field }) => <input type="hidden" {...field} />}
            />
            <Controller
              control={control}
              name="copyFromEquipmentCodeId"
              render={({ field }) => <input type="hidden" {...field} />}
            />
          </>
        )}
      </div>
    </div>
  );
};

EquipmentCode.propTypes = {
  isNew: PropTypes.bool,
  errors: PropTypes.shape(Object).isRequired,
  control: PropTypes.shape(Object).isRequired,
  setValue: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  year: PropTypes.number,
  program: PropTypes.string,
  nameValue: PropTypes.string.isRequired,
  clearErrors: PropTypes.func.isRequired,
};

export default EquipmentCode;
